<template>
  <div class="code-tag-editor">
    <v-tooltip top>
      <template v-slot:activator="{ on, attrs }">
        <v-btn :disabled="isDisabled" v-bind="attrs" v-on="on" @click="openDialog" color="primary" icon><v-icon>mdi-tag-multiple-outline</v-icon></v-btn>
      </template>
      <span>{{$t('edit_tags')}}</span>
    </v-tooltip>

    <v-dialog width="600" scrollable persistent v-model="dialog">
      <v-card  class="edit-tag-dialog" v-if="mode == 'edit' ">
        <v-card-title>
          {{$t('edit_tags')}}
          <v-spacer></v-spacer>
          <v-btn :disabled="hasLimit('tags') && filteredTags.length >= getLimit('tags')" color="primary" @click="mode = 'new' " text><v-icon class="mr-2">mdi-tag-plus-outline</v-icon>{{$t('new_tag')}}</v-btn>
        </v-card-title>

        <v-card-text class="py-5" v-if="filteredTags.length">
          <div v-if="codes.length == 1 || !showEditRadio" class="pl-2 pb-4 tag-hint font-weight-light">
            {{$t('empty_tag_hint')}}
          </div>
          <div v-if="codes.length > 1 && showEditRadio" class="pl-2 pb-4 tag-hint font-weight-light">
            {{$t('bulk_tag_edit_hint')}}
            <v-radio-group v-model="updateMode" class="radio-ctn font-weight-medium">
              <v-radio value="standard" :label="$t('ignore_empty_tags')"></v-radio>
              <v-radio value="clear-empty-tags" :label="$t('delete_empty_tags')"></v-radio>
            </v-radio-group>
          </div>

          <div class="d-flex tag-header font-weight-bold mx-3">
            <div class="name">{{$t('tag_name')}}</div>
            <div>{{$t('tag_value')}}</div>
            <v-spacer></v-spacer>
            <div v-if="hasLimit('tags')">{{filteredTags.length}}/{{getLimit('tags')}}</div>
          </div>
          <tag-input v-for="tag in filteredTags"
            :tag-value.sync="codeTags[tag.key]"
            :date-value="parsedDateValue(tag.key)"
            :date-error-message="dateFieldsErrors[tag.key]"
            :key="tag.key"
            :label="tag.key"
            :type="tag.type"
            :mode="mode"
            :rules="editRules"
            v-on:delete-tag="deleteTag(tag)"
            class="ma-2"
          ></tag-input>
        </v-card-text>

        <v-card-text class="py-5" v-if="!filteredTags.length">
          {{$t('tags_empty_state')}}
        </v-card-text>

        <v-card-text v-if="saveTagsErrors.length" class="red--text text-center">
          <div v-for="error in saveTagsErrors" :key="error">{{error}}</div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="close">{{$t('close')}}</v-btn>
          <v-btn text color="primary" v-if="!filteredTags.length && !tagsToDelete.length" @click="mode = 'new'" :loading="saving" >{{$t('ok')}}</v-btn>
          <v-btn text color="primary" v-if="filteredTags.length || tagsToDelete.length" @click="saveTags" :loading="saving" >{{$t('save')}}</v-btn>
        </v-card-actions>
      </v-card>

      <v-card class="edit-tag-dialog" v-if="mode == 'new' ">
        <v-card-title>
          {{$t('new_tag')}}
        </v-card-title>
        <v-card-text class="py-5">
          <div class="pb-5">
            <span v-html="$t('new_tag_hint')"></span>
          </div>
          <v-form ref="form" v-model="valid">
            <div class="tag-input d-flex">
              <div class="label font-weight-bold pt-2">{{$t('tag_name')}}</div>
              <v-text-field :counter="50" :placeholder="$t('tag_name_example')" dense single-line outlined v-model="form.key" :rules="newRules"></v-text-field>
            </div>
            <div class="errors-ctn">
              <div v-for="error in createTagErrors" :key="error">{{error}}</div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="goToEdit">{{$t('back')}}</v-btn>
          <v-btn text color="primary" @click="createTag" :loading="saving" >{{$t('create')}}</v-btn>
        </v-card-actions>
      </v-card>

    </v-dialog>
  </div>
</template>

<script>

import TagInput from '@/components/TagInput'
import { mapGetters } from 'vuex'
import amplitude from 'amplitude-js'
import Utils from '@/utils/utils'

export default {
  name: 'CodeTagEditor',
  components: {
    TagInput
  },
  computed: {
    ...mapGetters(['hasLimit', 'getLimit', 'isAdmin']),
    filteredTags () {
      return this.sortedTags.filter(tag => !tag.is_archived && !Utils.excludedTagRegEx.test(tag.key))
    },
    newRules () {
      return [
        v => !!v || this.$t('tag_name_required'),
        v => /^[\S]+$/.test(v) || this.$t('tag_name_no_space'),
        v => /^[a-zA-Z0-9_]*$/.test(v) || this.$t('tag_name_format'),
        v => v.length <= 50 || this.$t('tag_name_max_length'),
        ...(!this.isAdmin ? [v => !v.toLowerCase().startsWith('scantrust_') || this.$t('tag_name_scantrust')] : [])
      ]
    }
  },
  props: ['codes', 'tags', 'campaign', 'editMode', 'product', 'selectAll', 'count', 'isDisabled'],
  data () {
    return {
      showEditRadio: false,
      allCodes: [],
      batchSize: 10,
      dialog: false,
      saving: false,
      codeTags: {},
      updateMode: 'standard',
      sortedTags: [],
      mode: 'edit',
      createTagErrors: [],
      saveTagsErrors: [],
      dateFieldsErrors: {},
      tagsToDelete: [],
      editRules: [
        // v => (v && v.length <= 10) || 'Tag value cannot exceed 100 characters'
      ],
      valid: false,
      form: {
        key: '',
        name: '',
        type: 'text',
        // position?
        is_public: true,
        campaign: '',
        is_lu: false
      }
    }
  },
  watch: {
    codeTags: {
      deep: true,
      handler (next) {
        // Clear error under date input as soon as the date becomes valid
        for (const key in this.dateFieldsErrors) {
          const val = next[key]
          if (this.isValidDateFormat(val)) {
            this.$delete(this.dateFieldsErrors, key)
          }
        }
      }
    }
  },
  methods: {
    resetState () {
      this.form.key = ''
      this.form.name = ''
      this.createTagErrors = []
      this.saveTagsErrors = []
    },

    goToEdit () {
      this.resetState()
      this.mode = 'edit'
    },

    openDialog () {
      this.resetState()
      // Check if some of the selected codes already have existing SCM data filled
      // If yes: We show the radio button to pick an edit more
      // If not: We hide the radio choice and keep it as default
      if (this.codes.length <= 20) {
        this.showEditRadio = this.codes.some((code) => {
          for (const [key] of Object.entries(code.scm_data)) {
            if (code.scm_data[key].length > 0) {
              return true
            }
          }
        })
      } else if (this.selectAll && this.codes.length > 20) {
        this.showEditRadio = true
      }

      if (this.codes.length > 1) {
        this.codeTags = {}
      } else {
        this.codeTags = JSON.parse(JSON.stringify(this.codes[0].scm_data))
      }
      this.sortTags()
      this.validateDateFields()
      amplitude.getInstance().logEvent('click edit tags')
      this.dialog = true
    },

    sortTags () {
      const tagsWithVal = this.tags.filter(tag => this.codeTags[tag.key])
      const tagsWithoutVal = this.tags.filter(tag => !this.codeTags[tag.key])
      this.sortedTags = tagsWithVal.concat(tagsWithoutVal)
    },

    alignCodeTagsWithFilteredTags () {
      const activeKeys = this.filteredTags.map(tag => tag.key)

      if (!Object.keys(this.codeTags).length) {
        // backend doesn't accept empty scm_data object
        activeKeys.forEach(key => {
          this.codeTags[key] = ''
        })
      } else {
        // remove archived tags
        Object.keys(this.codeTags).forEach(key => {
          if (!activeKeys.includes(key)) delete this.codeTags[key]
        })
      }
    },

    async deleteTag (tag) {
      tag.is_archived = true
      this.sortTags()
      this.tagsToDelete.push(tag)
    },

    async createTag () {
      this.$refs.form.validate()
      if (!this.valid) {
        return
      }
      this.saving = true
      this.form.campaign = this.campaign
      this.form.name = this.form.key
      try {
        const found = this.tags.find((tag) => {
          return tag.key === this.form.key
        })

        let tag = {}
        if (found && found.id && found.is_archived) {
          this.form.is_archived = false
          this.form.id = found.id
          tag = await this.$store.dispatch('updateTag', { tag: this.form })
          if (this.tagsToDelete.length) {
            const matchIndex = this.tagsToDelete.findIndex((tag) => {
              return tag.id === found.id
            })
            if (~matchIndex) {
              this.tagsToDelete.splice(matchIndex, 1)
            }
          }
        } else {
          tag = await this.$store.dispatch('createTag', { tag: this.form })
        }

        amplitude.getInstance().logEvent('create tag')
        this.resetState()
        this.sortedTags.push(tag)
        this.mode = 'edit'
      } catch (err) {
        this.createTagErrors = []
        if (err.response && err.response.data) {
          for (const [key] of Object.entries(err.response.data)) {
            err.response.data[key].forEach((msg) => {
              if (key !== 'non_field_errors') {
                this.createTagErrors.push(msg)
              } else {
                this.createTagErrors.push(this.$t('create_tag_error'))
              }
            })
          }
        }
      } finally {
        this.saving = false
      }
    },

    close () {
      this.codeTags = {}
      if (this.tagsToDelete.length) {
        this.tagsToDelete.forEach((tag) => {
          tag.is_archived = false
        })
      }
      this.tagsToDelete = []
      this.dialog = false
      this.mode = 'edit'
    },

    async uploadBatch (index, isRetry) {
      // Take next BatchSize Products
      let array
      if (this.allCodes.length) {
        array = JSON.parse(JSON.stringify(this.allCodes))
      } else {
        array = JSON.parse(JSON.stringify(this.codes))
      }
      array = array.splice(index, this.batchSize)

      if (!array.length) {
        this.saving = false
        this.close()
        this.$emit('update-done')
        return
      }
      try {
        // delete batch to not set it on multiple codes
        delete this.codeTags.batch
        await this.$store.dispatch('bulkTransferAndUpdate', { codes: array, tags: this.codeTags })
        this.uploadBatch(index + this.batchSize)
      } catch (err) {
        if (err && err.response && err.response.status === 429) {
          this.throttling = true
          console.log('Throttling')
          setTimeout(() => {
            this.uploadBatch(index, true)
          }, 10000)
        }
      }
    },

    parsedDateValue (tagKey) {
      const val = this.codeTags[tagKey]
      return this.isValidDateFormat(val) ? val : null
    },

    isValidDateFormat (str) {
      if (str == null || str === '') return true

      // valid format is YYYY-MM-DD
      if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return false
      const [year, month, day] = str.split('-').map(Number)
      const date = new Date(Date.UTC(year, month - 1, day))
      return (
        date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day
      )
    },

    validateDateFields () {
      const errors = {}

      for (const tag of this.filteredTags) {
        const val = this.codeTags[tag.key]
        if (tag.type === 'date' && val && !this.isValidDateFormat(val)) {
          errors[tag.key] = this.$t('invalid_date_format')
        }
      }

      this.dateFieldsErrors = errors
      return !Object.keys(errors).length
    },

    async saveTags () {
      this.alignCodeTagsWithFilteredTags()
      if (!this.validateDateFields()) {
        this.saving = false
        return
      }

      this.saving = true
      amplitude.getInstance().logEvent('update tag values')
      try {
        if (this.tagsToDelete.length) {
          this.tagsToDelete.forEach((tag) => {
            this.$store.dispatch('updateTag', { tag })
          })
        }

        // CHANGE LOGIC TO LOOP
        if (this.editMode === 'bulk') {
          if (this.codes.length > 1 && this.updateMode === 'clear-empty-tags') {
            let test = {}
            this.tags.forEach((tag) => {
              if (!tag.key.startsWith('scantrust_')) {
                test[tag.key] = ''
              }
            })
            test = {
              ...test,
              ...this.codeTags
            }
            this.codeTags = test
          }

          // Remove existing tag value for removed tags
          if (this.tagsToDelete.length) {
            this.tagsToDelete.forEach((tag) => {
              this.codeTags[tag.key] = ''
            })
          }

          // If there are more code to update than the one provided as parameters
          if (this.selectAll && this.count > 20) {
            console.log('more than 20 codes selected, loading all')
            this.allCodes = await this.$store.dispatch('loadAllCodes', { count: this.count ? this.count : 0, campaign: this.campaign, product: this.product })
            this.uploadBatch(0)
          } else {
            console.log('less than 20')
            this.uploadBatch(0)
          }
        } else {
          // Remove existing tag value for removed tags
          if (this.tagsToDelete.length) {
            this.tagsToDelete.forEach((tag) => {
              this.codeTags[tag.key] = ''
            })
          }

          await this.$store.dispatch('saveTagsValue', { tags: this.codeTags, code: this.codes[0] })
          this.close()
        }
      } catch (err) {
        this.saveTagsErrors = []
        const scmData = err?.response?.data?.scm_data

        if (Array.isArray(scmData)) {
          scmData.forEach(msg => this.saveTagsErrors.push(String(msg)))
        } else if (scmData && typeof scmData === 'object' && Object.entries(scmData).length) {
          for (const [key, val] of Object.entries(scmData)) {
            this.saveTagsErrors.push(`${key}: ${String(val)}`)
          }
        } else if (err?.response?.data?.message || err?.message) {
          this.saveTagsErrors.push(String(err?.response?.data?.message || err?.message))
        } else {
          this.saveTagsErrors.push(this.$t('save_tags_error'))
        }
      } finally {
        this.saving = false
      }
    }
  }
}

</script>

<style lang="sass" scoped>

.edit-tag-dialog
  .tag-hint
    color: black
    font-size: 17px

  .radio-ctn
    label
      color: black !important

  .tag-input
    .label
      padding: 0px 10px
      color: black
      min-width: 140px

  .tag-header
    border-bottom: 1px solid rgba(0,0,0,0.3)
    .name
      min-width: 150px

.errors-ctn
  color: red
  padding-left: 150px

</style>
